import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { themeGet } from 'styled-system';
import { Text, Flex, Box, Image, ExternalLink, NakedButton } from 'roo-ui/components';
import { pointsClub, pointsClubPlus } from 'roo-ui/logos';
import Modal from 'components/Modal';
import { useModal } from 'lib/hooks';
import { mediaQuery } from 'lib/styledSystem';
import { useDataLayer } from 'hooks/useDataLayer';
import { openPointsClubModal } from 'store/ui/uiActions';

const PointsClubImage = styled(Image)`
  width: 60px;
  height: 60px;
  margin: 0 ${themeGet('space.6')} ${themeGet('space.2')} 0;
  ${mediaQuery.minWidth.md} {
    margin-bottom: 0;
  }
`;

const StyledExternalLink = styled(ExternalLink)`
  text-decoration: underline;
  color: ${themeGet('colors.brand.primary')};
  font-size: ${themeGet('fontSizes.sm')};
  ${mediaQuery.minWidth.md} {
    font-size: ${themeGet('fontSizes.base')};
  }
  &:hover {
    color: ${themeGet('colors.maroon')};
  }
`;

const StyledText = styled(Text)`
  font-size: ${themeGet('fontSizes.sm')};
  ${mediaQuery.minWidth.md} {
    font-size: ${themeGet('fontSizes.base')};
  }
`;

const BoxWithBorder = styled(Box)`
  margin: ${themeGet('space.2')} 0 ${themeGet('space.6')} 0;
  padding-bottom: ${themeGet('space.6')};
  border-bottom: 1px solid ${themeGet('colors.greys.alto')};
  ${mediaQuery.minWidth.md} {
    margin: 0 0 ${themeGet('space.7')} 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const PointsClubModal = ({ children, displayLearnMore }) => {
  const { openModal, closeModal, modalProps } = useModal();
  const dispatch = useDispatch();

  const { emitInteractionEvent } = useDataLayer();

  const openModalHandler = useCallback(() => {
    dispatch(openPointsClubModal());
    openModal();
  }, [openModal, dispatch]);

  const closeModalHandler = useCallback(() => {
    emitInteractionEvent({ type: 'Points Club Pop Up', value: 'Dismiss Selected' });
    closeModal();
  }, [emitInteractionEvent, closeModal]);

  const learnMoreHandler = useCallback(() => {
    emitInteractionEvent({ type: 'Points Club Pop Up', value: 'Learn More Link Selected' });
  }, [emitInteractionEvent]);

  return (
    <Fragment>
      <NakedButton onClick={openModalHandler} data-testid="points-club-modal-button">
        {children}
      </NakedButton>
      <Modal {...modalProps} onRequestClose={closeModalHandler} padding={[5, 12]} title="What is Points Club?">
        <BoxWithBorder>
          <StyledText data-testid="points-club-modal-copy">
            We created Points Club to recognise members who earn a high amount of points on the ground. This could be from spending on your
            credit card, grocery shopping or your energy bills.
          </StyledText>
        </BoxWithBorder>
        <Flex mb={[6, 7]} flexDirection={['column', 'row']}>
          <PointsClubImage src={pointsClub} alt="Points Club Logo" />
          <StyledText>
            As a Points Club member, you&apos;ll unlock&nbsp;
            <StyledText fontWeight="bold">25% bonus points</StyledText>
            &nbsp;on bookings with Qantas Hotels.*
          </StyledText>
        </Flex>
        <Flex mb={[6, 9]} flexDirection={['column', 'row']}>
          <PointsClubImage src={pointsClubPlus} alt="Points Club Plus Logo" />
          <StyledText>
            As a member of Points Club Plus, you can enjoy&nbsp;
            <StyledText fontWeight="bold">50% bonus points</StyledText>
            &nbsp;on bookings with Qantas Hotels.*
          </StyledText>
        </Flex>
        {displayLearnMore && (
          <Box mb={[8, 11]}>
            <StyledExternalLink
              href="/au/en/frequent-flyer/status-and-clubs/points-club.html"
              onClick={learnMoreHandler}
              data-testid="points-club-modal-learn-more"
            >
              Learn more about Points Club
            </StyledExternalLink>
          </Box>
        )}
        <Text fontSize={['xs', 'base']} color="greys.steel">
          *Excludes Classic deals, Qantas Holidays bookings, and Airbnb bookings.
        </Text>
      </Modal>
    </Fragment>
  );
};

PointsClubModal.propTypes = {
  children: PropTypes.node.isRequired,
  displayLearnMore: PropTypes.bool,
};

PointsClubModal.defaultProps = {
  displayLearnMore: false,
};

export default PointsClubModal;
